import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { ArrowLeft, Home, Building2, Store, Warehouse, MapPin, Bed, Bath, Maximize, ChevronLeft, ChevronRight, Mail, Languages, MessageCircle, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import { useLanguage } from '@/contexts/LanguageContext';

const PropertyDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [inquiryDialogOpen, setInquiryDialogOpen] = useState(false);
  const [translating, setTranslating] = useState(false);
  const [translatedContent, setTranslatedContent] = useState(null);
  const [currentLanguage, setCurrentLanguage] = useState('original');
  const [inquiryForm, setInquiryForm] = useState({
    client_name: '',
    client_email: '',
    client_phone: '',
    message: ''
  });

  useEffect(() => {
    fetchProperty();
  }, [id]);

  const fetchProperty = async () => {
    try {
      const response = await axios.get(`${API}/properties/${id}`);
      setProperty(response.data);
    } catch (error) {
      console.error('Error fetching property:', error);
      toast.error('Failed to load property details');
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const handleTranslate = async (language) => {
    if (currentLanguage === language) {
      setCurrentLanguage('original');
      return;
    }

    setTranslating(true);
    try {
      const textToTranslate = `${property.title}\n\n${property.description}\n\nAmenities: ${property.amenities?.join(', ') || 'None'}`;
      
      const langMap = { thai: 'th', chinese: 'zh-cn', russian: 'ru' };
      const response = await axios.post(`${API}/translate`, {
        text: textToTranslate,
        target_language: langMap[language]
      });

      const translated = response.data.translated;
      const parts = translated.split('\n\n');
      
      setTranslatedContent({
        title: parts[0] || property.title,
        description: parts[1] || property.description,
        amenities: parts[2]?.replace('Amenities: ', '').split(', ') || property.amenities
      });
      
      setCurrentLanguage(language);
      const langNames = { thai: 'Thai', chinese: 'Chinese', russian: 'Russian' };
      toast.success(`Translated to ${langNames[language]}!`);
    } catch (error) {
      toast.error('Translation failed. Please try again.');
      console.error('Translation error:', error);
    } finally {
      setTranslating(false);
    }
  };

  const getPropertyIcon = (type) => {
    switch(type) {
      case 'House': return <Home className="w-6 h-6" />;
      case 'Condo': return <Building2 className="w-6 h-6" />;
      case 'Land': return <MapPin className="w-6 h-6" />;
      case 'Commercial': return <Warehouse className="w-6 h-6" />;
      case 'Retail': return <Store className="w-6 h-6" />;
      default: return <Home className="w-6 h-6" />;
    }
  };

  const formatPrice = (price, listingType, rentalPeriod) => {
    const formatted = new Intl.NumberFormat('en-US').format(price);
    if (listingType === 'Sale') {
      return `฿${formatted}`;
    } else {
      // For all rentals, display /Month
      return `฿${formatted}/Month`;
    }
  };

  const nextImage = () => {
    const totalMedia = (property?.images?.length || 0) + (property?.videos?.length || 0);
    if (totalMedia > 0) {
      setCurrentImageIndex((prev) => (prev + 1) % totalMedia);
    }
  };

  const prevImage = () => {
    const totalMedia = (property?.images?.length || 0) + (property?.videos?.length || 0);
    if (totalMedia > 0) {
      setCurrentImageIndex((prev) => (prev - 1 + totalMedia) % totalMedia);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0A0E27]">
        <div className="inline-block h-12 w-12 animate-spin rounded-full border-4 border-solid border-[#D4AF37] border-r-transparent"></div>
      </div>
    );
  }

  if (!property) return null;

  return (
    <div className="min-h-screen bg-[#0A0E27]">
      {/* Header */}
      <header className="crystal-glass fixed top-0 left-0 right-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div 
              className="flex items-center cursor-pointer"
              onClick={() => navigate('/')}
              data-testid="nok-logo"
            >
              <h1 className="font-heading text-xl md:text-2xl font-semibold text-[#D4AF37] tracking-tight">
                NOK <span className="text-white/40 font-light mx-1">|</span> <span className="text-white font-light text-base md:text-lg">Thailand Property</span>
              </h1>
            </div>
            <Button 
              onClick={() => navigate('/')} 
              variant="outline"
              className="border-white/20 text-white hover:bg-white/10 hover:border-white/30 rounded-full"
              data-testid="back-to-listings-button"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              {t('back_to_listings')}
            </Button>
          </div>
        </div>
      </header>

      {/* Property Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 pt-28">
        {/* Media Gallery */}
        <div className="mb-8">
          {(() => {
            const allMedia = [
              ...(property.images || []).map(url => ({ type: 'image', url })),
              ...(property.videos || []).map(url => ({ type: 'video', url })),
            ];
            const currentMedia = allMedia[currentImageIndex] || allMedia[0];
            const isVideo = currentMedia?.type === 'video';
            const isYouTube = isVideo && (currentMedia.url.includes('youtube.com') || currentMedia.url.includes('youtu.be'));
            const getYouTubeId = (url) => {
              const match = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
              return match ? match[1] : null;
            };

            return (
              <>
                <div className="relative h-96 md:h-[600px] rounded-2xl overflow-hidden bg-[#131A38] border border-white/10 shadow-2xl">
                  {allMedia.length > 0 ? (
                    <>
                      {isVideo ? (
                        isYouTube ? (
                          <iframe
                            src={`https://www.youtube.com/embed/${getYouTubeId(currentMedia.url)}?autoplay=0`}
                            className="w-full h-full"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                            data-testid="property-main-video"
                          />
                        ) : (
                          <video
                            src={currentMedia.url}
                            controls
                            className="w-full h-full object-contain bg-black"
                            data-testid="property-main-video"
                          />
                        )
                      ) : (
                        <img
                          src={currentMedia.url}
                          alt={property.title}
                          className="w-full h-full object-cover"
                          data-testid="property-main-image"
                        />
                      )}
                      {allMedia.length > 1 && (
                        <>
                          <button
                            onClick={prevImage}
                            className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white p-3 rounded-full shadow-lg"
                            data-testid="prev-image-button"
                          >
                            <ChevronLeft className="w-6 h-6 text-[#0A0E27]" />
                          </button>
                          <button
                            onClick={nextImage}
                            className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white p-3 rounded-full shadow-lg"
                            data-testid="next-image-button"
                          >
                            <ChevronRight className="w-6 h-6 text-[#0A0E27]" />
                          </button>
                          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                            {allMedia.map((_, idx) => (
                              <button
                                key={idx}
                                onClick={() => setCurrentImageIndex(idx)}
                                className={`w-2 h-2 rounded-full transition-all ${
                                  idx === currentImageIndex ? 'bg-white w-8' : 'bg-white/50'
                                }`}
                                data-testid={`image-indicator-${idx}`}
                              />
                            ))}
                          </div>
                        </>
                      )}
                    </>
                  ) : (
                    <div className="flex items-center justify-center h-full">
                      <Building2 className="w-24 h-24 text-[#D4AF37]/30" />
                    </div>
                  )}

                  <div className="absolute top-4 left-4 flex gap-2">
                    <span className="px-3 py-1 bg-[#D4AF37] text-[#0A0E27] text-[10px] font-semibold rounded-full uppercase tracking-wider flex items-center gap-1.5">
                      {getPropertyIcon(property.property_type)}
                      {property.property_type}
                    </span>
                    <span className="px-3 py-1 bg-white/10 backdrop-blur-md text-white text-[10px] font-semibold rounded-full uppercase tracking-wider">
                      {property.listing_type}
                    </span>
                  </div>

                  {property.banner && (
                    <div className="absolute top-0 right-0 overflow-hidden w-40 h-40 pointer-events-none">
                      <span className={`absolute top-9 -right-10 w-48 text-center text-sm font-bold py-2 rotate-45 shadow-xl ${
                        property.banner === 'HOT LISTING' ? 'bg-red-500 text-white' :
                        property.banner === 'SPECIAL PROMOTION' ? 'bg-orange-500 text-white' :
                        property.banner === 'PRICE DROP' ? 'bg-green-500 text-white' :
                        'bg-purple-500 text-white'
                      }`}>
                        {property.banner}
                      </span>
                    </div>
                  )}
                </div>

                {/* Thumbnail Gallery */}
                {allMedia.length > 1 && (
                  <div className="flex gap-4 mt-4 overflow-x-auto pb-2" data-testid="thumbnail-gallery">
                    {allMedia.map((media, idx) => (
                      media.type === 'video' ? (
                        <div
                          key={idx}
                          className={`relative h-24 w-36 flex-shrink-0 rounded-lg cursor-pointer border-2 bg-black flex items-center justify-center ${
                            idx === currentImageIndex ? 'border-[#D4AF37]' : 'border-white/10'
                          }`}
                          onClick={() => setCurrentImageIndex(idx)}
                          data-testid={`thumbnail-${idx}`}
                        >
                          <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                            <div className="w-0 h-0 border-t-[8px] border-t-transparent border-l-[14px] border-l-white border-b-[8px] border-b-transparent ml-1" />
                          </div>
                          <span className="absolute bottom-1 left-2 text-[9px] text-white/70 font-medium">VIDEO</span>
                        </div>
                      ) : (
                        <img
                          key={idx}
                          src={media.url}
                          alt={`${property.title} ${idx + 1}`}
                          className={`h-24 w-36 object-cover rounded-lg cursor-pointer border-2 flex-shrink-0 ${
                            idx === currentImageIndex ? 'border-[#D4AF37]' : 'border-white/10'
                          }`}
                          onClick={() => setCurrentImageIndex(idx)}
                          data-testid={`thumbnail-${idx}`}
                        />
                      )
                    ))}
                  </div>
                )}
              </>
            );
          })()}
        </div>

        {/* Property Details */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <div className="bg-[#131A38] rounded-2xl p-8 shadow-lg border border-white/5">
              <div className="flex justify-between items-start mb-4">
                <h1 className="font-heading text-4xl font-semibold text-white flex-1" data-testid="property-detail-title">
                  {currentLanguage === 'original' ? property.title : translatedContent?.title}
                </h1>
                <div className="flex gap-2 ml-4 flex-wrap">
                  {[
                    { key: 'thai', label: t('translate_thai') },
                    { key: 'chinese', label: t('translate_chinese') },
                    { key: 'russian', label: t('translate_russian') },
                  ].map(lang => (
                    <Button
                      key={lang.key}
                      onClick={() => handleTranslate(lang.key)}
                      disabled={translating}
                      variant={currentLanguage === lang.key ? 'default' : 'outline'}
                      size="sm"
                      className={currentLanguage === lang.key ? 'bg-[#D4AF37] text-[#0A0E27]' : 'border-white/20 text-white hover:bg-white/10'}
                    >
                      <Languages className="w-4 h-4 mr-1" />
                      {translating && currentLanguage !== lang.key ? '...' : lang.label}
                    </Button>
                  ))}
                </div>
              </div>
              <div className="flex items-center text-[#94A3B8] mb-6">
                <MapPin className="w-5 h-5 mr-2 text-[#D4AF37]" />
                <span className="text-lg">{property.city}, {property.location}</span>
              </div>

              <div className="gold-line w-full mb-6"></div>

              {(property.bedrooms || property.bathrooms || property.size_sqm) && (
                <div className="flex gap-8 mb-8 p-6 bg-[#0A0E27] rounded-xl border border-white/5">
                  {property.bedrooms && (
                    <div className="flex flex-col items-center">
                      <Bed className="w-8 h-8 text-[#D4AF37] mb-2" />
                      <span className="text-2xl font-bold text-white">{property.bedrooms}</span>
                      <span className="text-sm text-[#94A3B8]">{t('bedrooms')}</span>
                    </div>
                  )}
                  {property.bathrooms && (
                    <div className="flex flex-col items-center">
                      <Bath className="w-8 h-8 text-[#D4AF37] mb-2" />
                      <span className="text-2xl font-bold text-white">{property.bathrooms}</span>
                      <span className="text-sm text-[#94A3B8]">{t('bathrooms')}</span>
                    </div>
                  )}
                  {property.size_sqm && (
                    <div className="flex flex-col items-center">
                      <Maximize className="w-8 h-8 text-[#D4AF37] mb-2" />
                      <span className="text-2xl font-bold text-white">{property.size_sqm}</span>
                      <span className="text-sm text-[#94A3B8]">{t('square_meters')}</span>
                    </div>
                  )}
                </div>
              )}

              <div className="mb-8">
                <h2 className="font-heading text-2xl font-semibold text-white mb-4">{t('description')}</h2>
                <p className="text-[#94A3B8] leading-relaxed" data-testid="property-description">
                  {currentLanguage === 'original' ? property.description : translatedContent?.description}
                </p>
              </div>

              {property.amenities && property.amenities.length > 0 && (
                <div>
                  <h2 className="font-heading text-2xl font-semibold text-white mb-4">{t('amenities')}</h2>
                  <div className="flex flex-wrap gap-2" data-testid="property-amenities">
                    {(currentLanguage === 'original' ? property.amenities : translatedContent?.amenities || property.amenities).map((amenity, idx) => (
                      <span 
                        key={idx}
                        className="px-4 py-2 bg-[#D4AF37]/10 text-[#D4AF37] rounded-full text-sm font-medium border border-[#D4AF37]/20"
                        data-testid={`amenity-${idx}`}
                      >
                        {amenity}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Pricing Card */}
          <div className="lg:col-span-1">
            <div className="bg-gradient-to-br from-[#D4AF37] to-[#B8962E] rounded-2xl p-8 shadow-2xl text-[#0A0E27] sticky top-24">
              <div className="mb-6">
                <div className="text-sm font-semibold mb-2 opacity-80">{t('price')}</div>
                <div className="font-heading text-5xl font-bold" data-testid="property-detail-price">
                  {formatPrice(property.price, property.listing_type, property.rental_period)}
                </div>
                {property.listing_type === 'Rental' && property.rental_period && (
                  <div className="text-sm mt-2 opacity-80">
                    Available for {property.rental_period} rental
                  </div>
                )}
              </div>

              <div className="w-full h-px bg-[#0A0E27]/20 mb-6"></div>

              <div className="space-y-3 mb-6">
                <div className="flex justify-between text-sm">
                  <span className="opacity-80">{t('property_type')}</span>
                  <span className="font-semibold">{property.property_type}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="opacity-80">{t('listing_type')}</span>
                  <span className="font-semibold">{property.listing_type}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="opacity-80">{t('status')}</span>
                  <span className="font-semibold capitalize">{property.status}</span>
                </div>
              </div>

              <Button 
                className="w-full bg-[#0A0E27] text-white hover:bg-[#131A38] font-bold text-lg py-6 rounded-full"
                data-testid="contact-us-button"
              >
                {t('contact_us')}
              </Button>

              <a
                href="https://lin.ee/soB6jcI"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-3 w-full py-4 mt-3 bg-[#06C755] hover:bg-[#05b34d] text-white font-semibold rounded-full transition-colors duration-300 text-sm"
                data-testid="property-line-nok"
              >
                <MessageCircle className="w-4 h-4" />
                LINE — NOK
                <ExternalLink className="w-3.5 h-3.5" />
              </a>

              <p className="text-xs text-center mt-4 opacity-75">
                {t('interested_msg')}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="border-t border-white/5 bg-[#07091B] mt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 text-center">
          <h3 className="font-heading text-2xl font-semibold text-[#D4AF37] mb-2">NOK</h3>
          <p className="text-[#94A3B8] mb-4">Thailand Property & Investment</p>
          <div className="gold-line w-24 mx-auto mb-4"></div>
          <p className="text-[#475569] text-sm">© {new Date().getFullYear()} NOK Thailand Property & Investment. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default PropertyDetail;